import { Button, Grid } from "@material-ui/core";
import React, { useCallback } from "react";
import { useDispatch } from "react-redux";
import { purchase } from "../../../../../api/schema";
import Table from "../../../../../components/Table";
import { openDialog } from "../../../../../modules/ui";
import columns from "./columns";
import Colors from '../../../../../theme/colors';
import formatDate from '../../../../../helpers/format/formatDate';

const ACTION_NOTE_PATTERN = /^\[(Hold|Cancel)\] (.+?) by (\S+?)(?:: (.*))?$/;

const formatActionNote = (adminNotes = "", rawStatus: string): string => {
  const tag = rawStatus === "hold" ? "Hold" : "Cancel";
  const match = adminNotes
    .split("\n")
    .reverse()
    .map(line => line.match(ACTION_NOTE_PATTERN))
    .find(result => result && result[1] === tag);
  if (!match) {
    return "";
  }
  const [, , when, who, note] = match;
  const verb = tag === "Hold" ? "Held" : "Cancelled";
  const localWhen = formatDate(
    when.replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}) UTC$/, "$1T$2:00.000Z")
  );
  return `${verb} by ${who}, ${localWhen}${note ? `: ${note}` : ""}`;
};

interface Props {
  purchases: Record<string, any>[];
}

export default ({ purchases }: Props): JSX.Element => {
  const dispatch = useDispatch();
  const handleResubmitButton = useCallback(({ fundingRequest, id }) => {
    dispatch(
      openDialog(
        "resubmit-funding-request",
        "Confirm",
        "Are you sure you want to resubmit this funding request?",
        {
          fundingRequest,
          fundingRequestPurchase: id
        }
      )
    );
  }, []);
  const handleFailedButton = useCallback(({ fundingRequest, id }) => {
    dispatch(
      openDialog(
        "mark-as-failed-funding-request-purchase",
        "Confirm",
        "Are you sure you want to mark this funding request purchase as failed?",
        {
          fundingRequestId: fundingRequest,
          fundingRequestPurchaseId: id,
          status: "failed"
        }
      )
    );
  }, []);
  const handlePurchaseActionButton = useCallback(
    (action, purchase) => {
      dispatch(
        openDialog("purchase-status-action", "", "", {
          action,
          fundingRequestId: purchase.fundingRequest,
          fundingRequestPurchaseId: purchase.id,
          category: purchase.category,
          amount: purchase.amount,
          account: purchase.accountLabel,
          scheduledDate: purchase.scheduledDate
        })
      );
    },
    []
  );
  const purchaseRows = purchases.map(purchase => {
    const actionNote = ["hold", "cancelled"].includes(purchase.rawStatus)
      ? formatActionNote(purchase.adminNotes, purchase.rawStatus)
      : "";
    return {
    ...purchase,
    status: (
      <>
        {purchase.status}
        {actionNote && (
          <div style={{ fontSize: 10, color: "#6B6B6B", lineHeight: 1.35 }}>
            {actionNote}
          </div>
        )}
      </>
    ),
    action:
        purchase?.can_submit_obligation ? (
            <p style={{ color: Colors.danger }}>
                Obligation Payment Failed
            </p>
        ) : ["Returned", "Failed"].includes(purchase.status) ? (
        <Grid>
          <Button
            variant="raised"
            size="medium"
            color="primary"
            onClick={() => handleResubmitButton(purchase)}
          >
            Resubmit
          </Button>
        </Grid>
      ) : ["paid", "misdirected"].includes(purchase.status.toLowerCase()) ? (
        <Grid>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={() => handleFailedButton(purchase)}
          >
            Mark as Failed
          </Button>
        </Grid>
      ) : purchase.rawStatus === "pending_for_time_batch" ? (
        <Grid>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={() => handlePurchaseActionButton("hold", purchase)}
          >
            Hold
          </Button>
        </Grid>
      ) : purchase.rawStatus === "hold" ? (
        <Grid container spacing={8} justify="flex-end" wrap="nowrap">
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              size="medium"
              onClick={() => handlePurchaseActionButton("release", purchase)}
            >
              Release To Batch
            </Button>
          </Grid>
          {purchase.canCancel && (
            <Grid item>
              <Button
                variant="contained"
                size="medium"
                style={{ backgroundColor: Colors.danger, color: Colors.white }}
                onClick={() => handlePurchaseActionButton("cancel", purchase)}
              >
                Cancel Payment
              </Button>
            </Grid>
          )}
        </Grid>
      ) : (
          <div/>
        )
    };
  });
  return <Table columns={columns} rows={purchaseRows} allowEmpty />;
};
